import { apiClient } from '@/services/apiClient';

/**
 * Client wrapper for `POST /console/asset_management/impact/staging-upload`.
 * Takes a single local file, ships it as multipart to MIS, and returns the
 * Brandfolder `fileUrl` (`object_url`) + echoed `filename` needed by
 * step 2 (`impact/upload`).
 *
 * Spec: docs/asset-management-upload-photo-sources.md §Step 1
 *
 * Constraints (enforced client-side too so we fail fast and don't waste
 * a multipart roundtrip):
 *   - max 50 MB (multer limit)
 *   - MIME must be image/jpeg, image/png, image/webp, image/gif
 */

export interface StagedFile {
  fileUrl: string;
  /** Server echoes multer `file.originalname`; we usually overwrite it
   *  client-side with the canonical RGF filename in step 2. */
  filename: string;
}

const STAGING_ENDPOINT = '/console/asset_management/impact/staging-upload';

const ALLOWED_MIME_TYPES = new Set<string>([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]);

const MAX_BYTES = 50 * 1024 * 1024;

interface StagingResponse {
  message: string;
  data: StagedFile;
}

/**
 * Throws a friendly `Error` when the file fails the documented client-side
 * checks. Use before invoking `stageFile` to avoid a doomed network roundtrip.
 */
export function validateStagingFile(file: File): string | null {
  if (!file) return 'No file selected.';
  if (file.size > MAX_BYTES) {
    return `${file.name} is larger than 50 MB.`;
  }
  const mime = file.type?.toLowerCase() ?? '';
  if (mime && !ALLOWED_MIME_TYPES.has(mime)) {
    return `${file.name} is not a supported image (JPEG, PNG, WebP, GIF).`;
  }
  return null;
}

export async function stageFile(file: File, signal?: AbortSignal): Promise<StagedFile> {
  const guardError = validateStagingFile(file);
  if (guardError) throw new Error(guardError);

  const body = new FormData();
  body.append('file', file, file.name);

  try {
    const { data } = await apiClient.post<StagingResponse>(STAGING_ENDPOINT, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
      signal,
    });
    if (!data?.data?.fileUrl) {
      throw new Error('Staging response missing fileUrl.');
    }
    return data.data;
  } catch (err: any) {
    // Surface the server's `{message}` envelope when available so the
    // user sees actionable copy instead of generic axios text.
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Could not stage file. Please try again.';
    const wrapped = new Error(message);
    (wrapped as any).status = err?.response?.status;
    (wrapped as any).serverError = err?.response?.data;
    throw wrapped;
  }
}

export const STAGING_LIMITS = {
  maxBytes: MAX_BYTES,
  allowedMimeTypes: Array.from(ALLOWED_MIME_TYPES),
} as const;
